import { useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { ErrorNote, Spinner } from '@/components/Layout';
import { Emblem } from '@/components/Brand';
import { QrImage } from '@/components/MemberCode';

interface CardPerson { id: string; first_name: string; last_name: string; member_number: number | null; member_code: string; dojo: { short_name: string | null; name: string } | null }

/** Credit card size, to laminate. */
const CARD = { w: '85.6mm', h: '54mm', qr: 150 };

/**
 * Members' cards to print: each with name, member number and their QR code. People
 * come from ?ids= (a household's own) or, for an admin's filtered list, the page state.
 * The database only returns people the signed-in person may see.
 */
export function PrintCards() {
  const [params] = useSearchParams();
  const location = useLocation();
  const ids = (location.state as { ids?: string[] } | null)?.ids ?? (params.get('ids') ?? '').split(',').filter(Boolean);
  const people = useQuery({
    queryKey: ['print-cards', ids.join()],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('person').select('id,first_name,last_name,member_number,member_code,dojo:home_dojo_id(short_name,name)').in('id', ids.slice(0, 500)).order('last_name').order('first_name');
      if (error) throw error;
      return data as unknown as CardPerson[];
    },
  });

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-stone-200 bg-white px-4 py-3 print:hidden">
        <button type="button" className="text-sm text-ink-500 hover:underline" onClick={() => history.back()}>← Back</button>
        <button className="btn-primary" onClick={() => window.print()} disabled={!people.data?.length}><Printer size={16} /> Print</button>
        <p className="text-sm text-ink-500">{people.data?.length ?? 0} {people.data?.length === 1 ? 'card' : 'cards'}. Credit card size. Cut along the lines and laminate.</p>
      </div>
      <div className="p-6 print:p-0">
        {!ids.length ? <ErrorNote>No one chosen to print.</ErrorNote> : people.isLoading ? <Spinner /> : people.error ? <ErrorNote>Could not load the cards.</ErrorNote> : (
          <div className="flex flex-wrap gap-2 print:gap-0">
            {(people.data ?? []).map(p => (
              <div key={p.id} className="flex items-center gap-3 overflow-hidden border border-dashed border-stone-300 bg-white p-[3mm]" style={{ width: CARD.w, height: CARD.h, breakInside: 'avoid' }}>
                <QrImage code={p.member_code} size={CARD.qr} className="h-full w-auto shrink-0" />
                <div className="min-w-0 space-y-1 leading-tight">
                  <div className="flex items-center gap-1.5"><Emblem size={20} /><span className="text-[9px] font-bold uppercase tracking-wide text-brand-600">EGKA member</span></div>
                  <p className="text-[14px] font-bold">{p.first_name} {p.last_name}</p>
                  <p className="text-[10px] text-ink-700">{p.member_number != null && <>No. {p.member_number}<br /></>}{p.dojo && <>{p.dojo.short_name ?? p.dojo.name}</>}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
